import React from 'react';

// IE and Firefox don't support this check
// https://caniuse.com/#feat=css-media-interaction
export const canHover =
  // https://github.com/rafrex/detect-hover/blob/master/src/index.js
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(any-hover: hover)').matches;

// IE, Edge and desktop Safari don't support this check
// https://caniuse.com/#search=touch
export const canTouch = 'ontouchstart' in window;

export const cannotHoverAndCanTouch = !canHover && canTouch;

export const requestIdleCallback = (window.requestIdleCallback =
  window.requestIdleCallback ||
  function (cb) {
    let start = Date.now();
    return setTimeout(function () {
      cb({
        didTimeout: false,
        timeRemaining: function () {
          return Math.max(0, 50 - (Date.now() - start));
        },
      });
    }, 1);
  });

window.cancelIdleCallback =
  window.cancelIdleCallback ||
  function (id) {
    clearTimeout(id);
  };

/**
 * Get the current browser
 */
export function getBrowser(): string {
  /* istanbul ignore if */
  if (typeof window === 'undefined') {
    return 'node';
  }

  // Opera 8.0+
  if (
    // @ts-expect-error checking vendor specific properties
    (!!window.opr && !!window.opr.addons) ||
    // @ts-expect-error checking vendor specific properties
    !!window.opera ||
    navigator.userAgent.indexOf(' OPR/') >= 0
  ) {
    return 'opera';
  }

  // Firefox 1.0+
  // @ts-expect-error checking vendor specific properties
  if (typeof window.InstallTrigger !== 'undefined') {
    return 'firefox';
  }

  // Chrome 1+
  if (
    // @ts-expect-error checking vendor specific properties
    !!window.chrome &&
    // @ts-expect-error checking vendor specific properties
    (!!window.chrome.webstore || !!window.chrome.runtime)
  ) {
    if (navigator.userAgent.indexOf('Edg') != -1) {
      return 'chromium-edge';
    }

    return 'chrome';
  }

  if (/Version\/([0-9._]+).*Safari/.test(navigator.userAgent)) {
    return 'safari';
  }

  // @ts-expect-error checking vendor specific properties
  if (document.documentMode) {
    return 'ie';
  }

  // @ts-expect-error checking vendor specific properties
  if (window.StyleMedia) {
    return 'edge';
  }

  return navigator.userAgent;
}
export function browserIs(browsers: Array<string>): boolean {
  return browsers.indexOf(getBrowser()) > -1;
}
export const refreshPopoversPositions = (): void => {
  // gigantic hack for https://github.com/SweetProcess/sweetprocess.com/issues/4981
  if (window.CustomEvent != null) {
    // bye IE
    window.dispatchEvent(new CustomEvent('scroll'));
  }
};

/** Hook to detect printing
 * borrowed from https://github.com/gregnb/use-detect-print/blob/e26285dea4949269ff22d113901b80da257d4489/src/index.js
 */
export function useDetectPrint() {
  const [isPrinting, toggleStatus] = React.useState(false);

  React.useEffect(() => {
    const printMq =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('print');
    const mqEvent = (mqList) => toggleStatus(!!mqList.matches);

    if (printMq) {
      printMq.addListener(mqEvent);
      return () => printMq.removeListener(mqEvent);
    }
  });

  return isPrinting;
}

export function getScrollableParent(node: HTMLElement | null): HTMLElement {
  const isElement = node instanceof HTMLElement;
  const overflowY = isElement && window.getComputedStyle(node).overflowY;
  const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';

  if (!node) {
    return document.body;
  } else if (isScrollable && node.scrollHeight >= node.clientHeight) {
    return node;
  }

  return getScrollableParent(node.parentElement) || document.body;
}

export function detectPrintHOC<P extends {printing: boolean}>(
  WrappedComponent: React.ComponentType<P>
): React.ComponentType<Omit<P, 'printing'>> {
  return function DetectPrintHOC(props: P) {
    const isPrinting = useDetectPrint();

    return <WrappedComponent {...props} printing={isPrinting} />;
  };
}
